/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import { defineStore } from 'pinia'
import { ref, toRaw } from 'vue'

import userAPI from '@/io.ox/core/api/user'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { settings as mailSettings } from '@/io.ox/mail/settings'
import { settings as calendarSettings } from '@/io.ox/calendar/settings'
import locale from '@/io.ox/core/locale'
import theming from '@/io.ox/core/theming/main'
import ox from '@/ox'
import { getCustomDisplayNameForAddress } from '@/io.ox/mail/util'
import { hasFeature } from '@/io.ox/core/feature'
import yell from '@/io.ox/core/yell'

const FEATURE_MANDATORY_FIELDS = {
  mandatoryAccountRecovery: ['recoveryEmail'],
  mandatoryPersonalData: ['firstName', 'lastName']
}

export const useFirstStartWizardStore = defineStore('first-start-wizard', () => {
  let userModel

  const userData = ref({})
  const initialSnapshot = ref({})

  const initialLanguage = locale.current()
  const initialTimezone = coreSettings.getTimezone()
  const initialThemeId = theming.getCurrentTheme().id
  const { id: initialAccentColorId = null } = theming.getCurrentAccentColor()

  function getMandatoryFields () {
    return Object.entries(FEATURE_MANDATORY_FIELDS)
      .filter(([feature]) => hasFeature(feature))
      .flatMap(([, fields]) => fields)
  }

  function isMandatory (fieldName) {
    return getMandatoryFields().includes(fieldName)
  }

  function hasMandatoryFields () {
    return getMandatoryFields().length > 0
  }

  function isFieldFilled (value) {
    if (typeof value === 'string') return value.trim() !== ''
    return value != null
  }

  function areMandatoryFieldsFilled () {
    const mandatoryFields = getMandatoryFields()
    return mandatoryFields.every(field => isFieldFilled(userData.value[field]))
  }

  function wereMandatoryFieldsPersisted () {
    const mandatoryFields = getMandatoryFields()
    return mandatoryFields.every(field => isFieldFilled(initialSnapshot.value[field]))
  }

  function hasChanges () {
    // Exclude display-only fields: avatarUrl changes independently via image events,
    // displayNamePlaceholder is a computed fallback and not user-editable
    const { avatarUrl, displayNamePlaceholder, ...current } = userData.value
    const { avatarUrl: _a, displayNamePlaceholder: _d, ...initial } = initialSnapshot.value
    return JSON.stringify(current) !== JSON.stringify(initial)
  }

  async function initUserData () {
    userModel = await userAPI.getCurrentUser()
    const id = userModel.get('id')
    const email = userModel.get('email1')
    const firstName = userModel.get('first_name')
    const lastName = userModel.get('last_name')
    const displayName = getCustomDisplayNameForAddress(email)

    Object.assign(userData.value, {
      timezone: coreSettings.getTimezone(),
      language: locale.current(),
      theme: theming.getCurrentTheme().id,
      accentColorId: theming.getCurrentAccentColor().id ?? null,
      mailLayout: mailSettings.get('layout', 'vertical'),
      workweekStart: calendarSettings.get('workweekStart'),
      numDaysWorkweek: calendarSettings.get('numDaysWorkweek'),
      startTime: calendarSettings.get('startTime'),
      endTime: calendarSettings.get('endTime'),
      firstName,
      lastName,
      email,
      avatarUrl: userModel.get('image1_url'),
      displayName
    })

    userAPI.off(`reset:image:${id} update:image:${id}`).on(`reset:image:${id} update:image:${id}`, () => {
      userAPI.getCurrentUser().then(userdata => {
        userData.value.avatarUrl = userdata.get('image1_url')
      })
    })

    initialSnapshot.value = JSON.parse(JSON.stringify(userData.value))
  }

  async function saveSettings () {
    try {
      // language / region
      locale.setLocaleData(userData.value.language)
      coreSettings.set('language', userData.value.language)
      coreSettings.set('timezone', userData.value.timezone)

      // personal info
      userModel.set({
        first_name: userData.value.firstName,
        last_name: userData.value.lastName,
        display_name: userData.value.displayName || userData.value.displayNamePlaceholder
      })
      mailSettings.set('customDisplayNames', toRaw(userData.value.customDisplayNames))

      // theme preference
      theming.setAccentColorByUser(userData.value.accentColorId)
      theming.setTheme(userData.value.theme)
      theming.saveCurrent()

      // mail layout
      mailSettings.set('layout', userData.value.mailLayout)

      // calendar working time
      calendarSettings.set('workweekStart', userData.value.workweekStart)
      calendarSettings.set('numDaysWorkweek', userData.value.numDaysWorkweek)
      calendarSettings.set('startTime', userData.value.startTime)
      calendarSettings.set('endTime', userData.value.endTime)

      await Promise.all([
        userModel.save(),
        mailSettings.save(undefined, { force: true }),
        calendarSettings.save(undefined, { force: true }),
        coreSettings.save(undefined, { force: true })
      ])

      // reload page if language or timezone changed
      if (userData.value.language !== initialLanguage || userData.value.timezone !== initialTimezone) {
        location.reload()
      } else {
        ox.trigger('change:customDisplayNames')
      }
    } catch (error) {
      yell(error)
      throw error
    }
  }

  function resetSettings () {
    theming.setAccentColorByUser(initialAccentColorId)
    theming.setTheme(initialThemeId)
  }

  return {
    userData,
    initialSnapshot,
    initUserData,
    saveSettings,
    resetSettings,
    isMandatory,
    hasMandatoryFields,
    areMandatoryFieldsFilled,
    wereMandatoryFieldsPersisted,
    hasChanges
  }
})
